/**
 * TableBrowser — Reusable component for browsing, searching, selecting, and
 * previewing tables from any data source (connectors, workspace, exports).
 *
 * Features:
 *  - Paginated table list with search
 *  - Expandable rows showing column list with type badges
 *  - Per-table column selection (opt-in on expand)
 *  - Inline sample-data preview
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Visibility as PreviewIcon,
  VisibilityOff as HidePreviewIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  Storage as TableIcon,
} from '@mui/icons-material';
import { getTypeIcon, dtypeLabel } from './columnTypeUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TableColumnInfo {
  name: string;
  dtype: string;
  nullable: boolean;
}

export interface TableEntry {
  name: string;
  schemaName?: string;
  rowCountEstimate?: number;
  columnCount?: number;
  columns?: TableColumnInfo[];
  previewRows?: any[];
  previewLoading?: boolean;
  columnsLoading?: boolean;
}

export interface TableBrowserProps {
  tables: TableEntry[];
  loading: boolean;
  totalCount: number;
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  // Selection
  selectionMode: 'single' | 'multi' | 'none';
  selectedTables: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  // Column selection (optional — enabled per-table on expand)
  columnSelectionEnabled?: boolean;
  selectedColumns?: Map<string, string[]>;
  onColumnSelectionChange?: (table: string, cols: string[]) => void;
  // Lazy-loading callbacks
  onExpandTable?: (tableName: string) => void;
  onPreviewTable?: (tableName: string) => void;
  // Pagination
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TableBrowser: React.FC<TableBrowserProps> = ({
  tables,
  loading,
  totalCount,
  searchValue,
  onSearchChange,
  selectionMode,
  selectedTables,
  onSelectionChange,
  columnSelectionEnabled = false,
  selectedColumns,
  onColumnSelectionChange,
  onExpandTable,
  onPreviewTable,
  page,
  pageSize,
  onPageChange,
}) => {
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [previewVisible, setPreviewVisible] = useState<Set<string>>(new Set());

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Toggle table selection
  const toggleTable = useCallback(
    (name: string) => {
      if (selectionMode === 'none') return;
      const next = new Set(selectedTables);
      if (selectionMode === 'single') {
        if (next.has(name)) {
          next.delete(name);
        } else {
          next.clear();
          next.add(name);
        }
      } else {
        if (next.has(name)) next.delete(name);
        else next.add(name);
      }
      onSelectionChange(next);
    },
    [selectionMode, selectedTables, onSelectionChange],
  );

  // Select / deselect all on current page
  const toggleAll = useCallback(() => {
    if (selectionMode !== 'multi') return;
    const pageNames = tables.map((t) => t.name);
    const allSelected = pageNames.every((n) => selectedTables.has(n));
    const next = new Set(selectedTables);
    if (allSelected) {
      pageNames.forEach((n) => next.delete(n));
    } else {
      pageNames.forEach((n) => next.add(n));
    }
    onSelectionChange(next);
  }, [selectionMode, tables, selectedTables, onSelectionChange]);

  const allPageSelected = useMemo(
    () => tables.length > 0 && tables.every((t) => selectedTables.has(t.name)),
    [tables, selectedTables],
  );

  // Expand / collapse row
  const toggleExpand = useCallback(
    (name: string) => {
      setExpandedTables((prev) => {
        const next = new Set(prev);
        if (next.has(name)) {
          next.delete(name);
        } else {
          next.add(name);
          onExpandTable?.(name);
        }
        return next;
      });
    },
    [onExpandTable],
  );

  // Toggle inline preview
  const togglePreview = useCallback(
    (name: string) => {
      setPreviewVisible((prev) => {
        const next = new Set(prev);
        if (next.has(name)) {
          next.delete(name);
        } else {
          next.add(name);
          onPreviewTable?.(name);
        }
        return next;
      });
    },
    [onPreviewTable],
  );

  // Column toggle
  const toggleColumn = useCallback(
    (table: string, column: string) => {
      if (!onColumnSelectionChange || !selectedColumns) return;
      const current = selectedColumns.get(table) ?? [];
      const next = current.includes(column)
        ? current.filter((c) => c !== column)
        : [...current, column];
      onColumnSelectionChange(table, next);
    },
    [selectedColumns, onColumnSelectionChange],
  );

  const selectAllColumns = useCallback(
    (table: string, allCols: string[]) => {
      onColumnSelectionChange?.(table, allCols);
    },
    [onColumnSelectionChange],
  );

  const deselectAllColumns = useCallback(
    (table: string) => {
      onColumnSelectionChange?.(table, []);
    },
    [onColumnSelectionChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Search bar */}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Search tables..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            sx: { fontSize: 13 },
          }}
        />
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 11 }}>
          {totalCount} table{totalCount !== 1 ? 's' : ''}
        </Typography>
      </Stack>

      {/* Select-all bar */}
      {selectionMode === 'multi' && tables.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={allPageSelected ? 'Deselect page' : 'Select page'}>
            <IconButton size="small" onClick={toggleAll} sx={{ p: 0.25 }}>
              {allPageSelected ? (
                <DeselectIcon sx={{ fontSize: 16 }} />
              ) : (
                <SelectAllIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
            {selectedTables.size} selected
          </Typography>
        </Stack>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', mt: 0.5 }}>
            Discovering tables...
          </Typography>
        </Box>
      )}

      {/* Empty state */}
      {!loading && tables.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <TableIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {searchValue ? 'No tables match your search' : 'No tables found'}
          </Typography>
        </Box>
      )}

      {/* Table list */}
      {!loading && tables.length > 0 && (
        <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
          {tables.map((tbl) => {
            const isSelected = selectedTables.has(tbl.name);
            const isExpanded = expandedTables.has(tbl.name);
            const showPreview = previewVisible.has(tbl.name);
            const tblSelectedCols = selectedColumns?.get(tbl.name);

            return (
              <Box key={tbl.name} sx={{ mb: 0.75 }}>
                {/* Row header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: isSelected ? alpha('#2563eb', 0.3) : 'divider',
                    bgcolor: isSelected ? alpha('#2563eb', 0.03) : 'transparent',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '&:hover': { borderColor: alpha('#2563eb', 0.2), bgcolor: alpha('#2563eb', 0.01) },
                  }}
                  onClick={() => toggleExpand(tbl.name)}
                >
                  {selectionMode !== 'none' && (
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleTable(tbl.name);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.25 }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontFamily: '"Fira Code", monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {tbl.name}
                      </Typography>
                      {tbl.schemaName && (
                        <Chip label={tbl.schemaName} size="small" sx={{ fontSize: 9, height: 16 }} />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      {tbl.rowCountEstimate != null && (
                        <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>
                          ~{tbl.rowCountEstimate.toLocaleString()} rows
                        </Typography>
                      )}
                      {tbl.columnCount != null && (
                        <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>
                          {tbl.rowCountEstimate != null ? ' · ' : ''}{tbl.columnCount} cols
                        </Typography>
                      )}
                      {columnSelectionEnabled && tblSelectedCols != null && tbl.columns && (
                        <Chip
                          label={`${tblSelectedCols.length}/${tbl.columns.length} cols`}
                          size="small"
                          sx={{ fontSize: 9, height: 16, ml: 0.5, bgcolor: alpha('#059669', 0.1), color: '#059669' }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Box>

                {/* Expanded content */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      ml: selectionMode !== 'none' ? 4.5 : 1,
                      mr: 1,
                      mt: 0.5,
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: '#fafbfc',
                    }}
                  >
                    {/* Columns loading */}
                    {tbl.columnsLoading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">Loading columns...</Typography>
                      </Box>
                    )}

                    {/* Column list */}
                    {tbl.columns && tbl.columns.length > 0 && (
                      <>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: 'text.secondary' }}>
                            Columns ({tbl.columns.length})
                          </Typography>
                          {columnSelectionEnabled && (
                            <>
                              <Button
                                size="small"
                                onClick={() => selectAllColumns(tbl.name, tbl.columns!.map((c) => c.name))}
                                sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 0.75, py: 0 }}
                              >
                                All
                              </Button>
                              <Button
                                size="small"
                                onClick={() => deselectAllColumns(tbl.name)}
                                sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 0.75, py: 0 }}
                              >
                                None
                              </Button>
                            </>
                          )}
                          <Box sx={{ flex: 1 }} />
                          <Tooltip title={showPreview ? 'Hide preview' : 'Preview data'}>
                            <IconButton
                              size="small"
                              onClick={() => togglePreview(tbl.name)}
                              sx={{ p: 0.25 }}
                            >
                              {showPreview ? (
                                <HidePreviewIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <PreviewIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                          {tbl.columns.map((col) => {
                            const isColSelected = tblSelectedCols
                              ? tblSelectedCols.includes(col.name)
                              : true; // default all selected
                            return (
                              <Stack
                                key={col.name}
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                  py: 0.25,
                                  px: 0.5,
                                  borderRadius: 0.5,
                                  opacity: columnSelectionEnabled && !isColSelected ? 0.5 : 1,
                                  '&:hover': { bgcolor: alpha('#2563eb', 0.04) },
                                }}
                              >
                                {columnSelectionEnabled && (
                                  <Checkbox
                                    size="small"
                                    checked={isColSelected}
                                    onChange={() => toggleColumn(tbl.name, col.name)}
                                    sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                                  />
                                )}
                                {getTypeIcon(col.dtype)}
                                <Typography sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace', flex: 1 }}>
                                  {col.name}
                                </Typography>
                                <Chip
                                  label={dtypeLabel(col.dtype)}
                                  size="small"
                                  sx={{ fontSize: 9, height: 16, fontWeight: 500 }}
                                />
                                {!col.nullable && (
                                  <Chip label="NOT NULL" size="small" variant="outlined" sx={{ fontSize: 8, height: 14 }} />
                                )}
                              </Stack>
                            );
                          })}
                        </Box>
                      </>
                    )}

                    {/* No columns */}
                    {tbl.columns && tbl.columns.length === 0 && !tbl.columnsLoading && (
                      <Typography variant="caption" color="text.secondary">
                        No column metadata available
                      </Typography>
                    )}

                    {/* Inline preview */}
                    <Collapse in={showPreview} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                        {tbl.previewLoading && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                            <CircularProgress size={14} />
                            <Typography variant="caption" color="text.secondary">Loading preview...</Typography>
                          </Box>
                        )}
                        {tbl.previewRows && tbl.previewRows.length > 0 && (
                          <TableContainer sx={{ maxHeight: 200, overflow: 'auto' }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  {Object.keys(tbl.previewRows[0]).map((col) => (
                                    <TableCell
                                      key={col}
                                      sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        fontFamily: '"Fira Code", monospace',
                                        py: 0.5,
                                        px: 1,
                                        bgcolor: '#f1f5f9',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 200,
                                      }}
                                    >
                                      {col}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {tbl.previewRows.map((row, idx) => (
                                  <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                                    {Object.values(row).map((val: any, ci) => (
                                      <TableCell
                                        key={ci}
                                        sx={{
                                          fontSize: 10,
                                          py: 0.25,
                                          px: 1,
                                          maxWidth: 200,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {val === null || val === undefined ? (
                                          <Chip label="null" size="small" sx={{ fontSize: 9, height: 16, color: '#94a3b8' }} />
                                        ) : (
                                          String(val)
                                        )}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                        {tbl.previewRows && tbl.previewRows.length === 0 && !tbl.previewLoading && (
                          <Typography variant="caption" color="text.secondary">No data</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => onPageChange(p)}
            size="small"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default TableBrowser;
